import React from 'react';

import {
  Chip,
  Container,
  Grid,
  Icon,
  Link,
  Typography,
  Paper,
} from '@material-ui/core';
import {
  GitHub as GitHubIcon,
  Storage as StorageIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { Link as ReactLink } from 'react-router-dom';

import HoverImage from 'react-hover-image';
import Header from '../components/Headers';
import Title from '../components/Title';
import Author from '../components/Author';
import Banner from '../components/Banner';
import Button from '../components/Button';
import Footer from '../components/Footer';
// import { DenoiserDemoComponent } from './demo';

const useStyles = makeStyles((theme) => ({
  main: {
    textAlign: 'center',
  },
  titleHead: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(4),
    fontFamily: 'Source Serif Pro',
    '& > a > *': {
      margin: theme.spacing(0.5),
    },
  },
  bibtexSpan: {
    backgroundColor: theme.palette.grey[200],
    marginTop: theme.spacing(2),
    padding: theme.spacing(1, 4),
  },
  iconText: {
    wordWrap: 'break-word',
  },
}));

export default function MainPage(): React.ReactElement {
  const classes = useStyles();
  const mainPageSections = [
    { title: 'Home', url: '#' },
    { title: 'Abstract', url: '#abstract' },
    { title: 'Paper', url: '#paper' },
    { title: 'Video', url: '#video' },
    { title: 'Downloadables', url: '#download' },
    { title: 'Demo', url: '#results' },
    { title: 'Resources', url: '#acknowledgments' },
    { title: 'References', url: '#reference' },
  ];
  const authorName = 'Ke-Chi Chang';
  const authorGithubProfile = 'https://github.com/arcchang1236';
  const githubPage = 'https://github.com/arcchang1236/CA-NoiseGAN';
  const paperName = 'Learning Camera-Aware Noise Models';

  return (
    <div className={classes.main}>
      <Header
        title={paperName}
        githubPage={githubPage}
        sections={mainPageSections}
      />
      <Container maxWidth="lg">
        <HeadingTeaser />
        <AbstractSection />
        <PaperSection />
        <DownloadSection />
        <ResultsSection />
        <AcknowledgmentsSection />
        <ReferencesSection />
      </Container>
      <Footer authorName={authorName} githubPage={authorGithubProfile} />
    </div>
  );
}

function HeadingTeaser(): React.ReactElement {
  const classes = useStyles();
  const topicLabels = [{ text: 'ECCV 2020', url: 'https://eccv2020.eu/' }];
  const imageUrl = `${process.env.PUBLIC_URL}/images/teaser.png`;

  return (
    <>
      <Typography
        component="h1"
        variant="h3"
        gutterBottom
        className={classes.titleHead}
      >
        Learning Camera-Aware Noise Models <br />
        {topicLabels.map((label) => (
          <Link
            key={label.text}
            href={label.url}
            target="_blank"
            rel="noopener"
          >
            <Chip label={label.text} />
          </Link>
        ))}
      </Typography>
      <AuthorSection />
      <Banner elevation={0} imageSrc={imageUrl} />
      <Button text="Download Code / Results" link="#download" />
    </>
  );
}

function AuthorSection(): React.ReactElement {
  const authors = [
    {
      name: 'Ke-Chi Chang',
      url: 'http://arcchang1236.github.io/',
      affiliation: '1,2',
    },
    {
      name: 'Ren Wang',
      url: 'https://tw.linkedin.com/in/ren-wang-61b273160',
      affiliation: '1',
    },
    {
      name: 'Hung-Jin Lin',
      url: 'https://github.com/leVirve',
      affiliation: '1',
    },
    {
      name: 'Yu-Lun Liu',
      url: 'https://www.cmlab.csie.ntu.edu.tw/~yulunliu',
      affiliation: '1',
    },
    {
      name: 'Chia-Ping Chen',
      url: 'https://tw.linkedin.com/in/chia-ping-chen-81674078',
      affiliation: '1',
    },
    {
      name: 'Yu-Lin Chang',
      url: 'https://scholar.google.com/citations?user=0O9rukQAAAAJ&hl=en',
      affiliation: '1',
    },
    {
      name: 'Hwann-Tzong Chen',
      url: 'https://htchen.github.io/',
      affiliation: '2',
    },
  ];

  const affiliations = [
    { number: '1', name: 'MediaTek Inc.', url: 'https://www.mediatek.tw/' },
    { number: '2', name: 'National Tsing Hua University', url: '' },
  ];
  return (
    <>
      <Author authors={authors} affiliations={affiliations} />
    </>
  );
}

function AbstractSection(): React.ReactElement {
  const imageUrl = `${process.env.PUBLIC_URL}/images/network.png`;

  return (
    <>
      <Title anchor="abstract" name="Abstract" />
      <Typography component="h3" variant="h6" align="left" paragraph>
        Modeling imaging sensor noise is a fundamental problem for image
        processing and computer vision applications. While most previous works
        adopt statistical noise models, real-world noise is far more complicated
        and beyond what these models can describe. To tackle this issue, we
        propose a data-driven approach, where a generative noise model is
        learned from real-world noise. The proposed noise model is camera-aware,
        that is, different noise characteristics of different camera sensors can
        be learned simultaneously, and a single learned noise model can generate
        different noise for different camera sensors. Experimental results show
        that our method quantitatively and qualitatively outperforms existing
        statistical noise models and learning-based methods.
      </Typography>
      <div style={{ maxWidth: 800, height: 'auto', margin: 'auto' }}>
        <Banner elevation={0} imageSrc={imageUrl} />
      </div>
    </>
  );
}

function PaperSection(): React.ReactElement {
  const classes = useStyles();
  const thumbImageUrl = `${process.env.PUBLIC_URL}/images/thumb.jpg`;
  const thumbTitle = `Learning Camera-Aware Noise Models, ECCV 2020 (arxiv)`;
  const arxivLink = `https://arxiv.org/abs/2008.09370`;
  const descriptionText = `Ke-Chi Chang, Ren Wang, Hung-Jin Lin, Yu-Lun Liu, Chia-Ping Chen, Yu-Lin Chang, and Hwann-Tzong Chen, "Learning Camera-Aware Noise Models", in Proceedings
  of the European Conference on Computer Vision (ECCV), 2020`;
  const bibtex = `@InProceedings{chang2020learning,
    author = {Chang, Ke-Chi and Wang, Ren and Lin, Hung-Jin and Liu, Yu-Lun and Chen, Chia-Ping and Chang, Yu-Lin and Chen, Hwann-Tzong},
    title={Learning Camera-Aware Noise Models},
    booktitle={Proceedings of European Conference on Computer Vision (ECCV)},
    year={2020}
}`;

  return (
    <>
      <Title anchor="paper" name="Paper" />
      <Chip label="Arxiv" variant="outlined" color="primary" />
      <Grid item>
        <Link href={arxivLink} target="_blank" rel="noopener">
          <Banner title={thumbTitle} elevation={0} imageSrc={thumbImageUrl} />
        </Link>
      </Grid>
      <Title anchor="citation" name="Citation" />
      <Typography align="left" variant="h6" color="inherit" gutterBottom>
        {descriptionText}
      </Typography>
      <Chip label="BibTeX" variant="outlined" color="primary" />
      <Paper elevation={0} className={classes.bibtexSpan}>
        <Typography align="left" variant="h6" color="inherit" gutterBottom>
          <pre style={{ wordWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
            {bibtex}
          </pre>
        </Typography>
      </Paper>
    </>
  );
}

function DownloadSection(): React.ReactElement {
  const youtubeLink = 'https://www.youtube.com/embed/_gScv9bAdTE';
  const githubProjectLink = 'https://github.com/arcchang1236/CA-NoiseGAN';
  const resultZipLink =
    'https://drive.google.com/drive/folders/1jnv9rKEv1uTO7uyAf5fQCBHPL_4PCrhj?usp=sharing';

  const iconLinksContent = [
    { name: 'Code', url: githubProjectLink, icon: GitHubIcon },
    // { name: 'Video', url: youtubeLink, icon: YouTubeIcon },
    // { name: 'Supplementary', url: supplementaryLink, icon: PictureAsPdfIcon },
    { name: 'Materials', url: resultZipLink, icon: StorageIcon },
  ];

  return (
    <>
      <Title anchor="video" name="Video" />
      <Grid container justify="center" spacing={1}>
        <iframe
          title="ytVideo"
          width="660"
          height="415"
          src={youtubeLink}
          frameBorder="0"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </Grid>
      <Title anchor="download" name="Downloadables" />
      <Grid container justify="center" spacing={1}>
        {iconLinksContent.map((iconLink) => (
          <Grid item xs={2} key={iconLink.url}>
            <Link href={iconLink.url} target="_blank" rel="noopener">
              <Icon
                component={iconLink.icon}
                color="action"
                style={{ fontSize: 60 }}
              />
              <Typography align="center" variant="h6" color="inherit">
                {iconLink.name}
              </Typography>
            </Link>
          </Grid>
        ))}
      </Grid>
    </>
  );
}

function ResultsSection(): React.ReactElement {
  const demoNRSrcUrl = `${process.env.PUBLIC_URL}/images/noise_model/0_2_6400_L_noisyg.jpg`;
  const demoNRTgtUrl = `${process.env.PUBLIC_URL}/images/noise_model/0_2_6400_L_clean.jpg`;
  const demoNRTitle = `Denoisers`;
  const demoNMSrcUrl = `${process.env.PUBLIC_URL}/images/noise_model/0_2_6400_L_clean.jpg`;
  const demoNMTgtUrl = `${process.env.PUBLIC_URL}/images/noise_model/0_2_6400_L_noise.jpg`;
  const demoNMTitle = `Noise Models`;
  const myStyle = {
    width: `100%`,
    padding: `40px`,
  };
  const myStyleLink = {
    fontSize: 20,
  };
  const demoLinksContent = [
    {
      mode: 'Noise Models',
      name: demoNMTitle,
      url: '/demo_nm',
      imgPath1: demoNMSrcUrl,
      imgPath2: demoNMTgtUrl,
    },
    {
      mode: 'Denoisers',
      name: demoNRTitle,
      url: '/demo_nr',
      imgPath1: demoNRSrcUrl,
      imgPath2: demoNRTgtUrl,
    },
  ];
  return (
    <>
      {/* <Title anchor="demo" name="Interactive Demo" />
      <MagnifierPackDenoiserDemoComponent /> */}
      <Title anchor="results" name="Demo" />
      <Grid container justify="center" spacing={1}>
        {demoLinksContent.map((demoLink) => (
          <Grid item xs={6} key={demoLink.url}>
            <ReactLink style={myStyleLink} to={demoLink.url}>
              {demoLink.mode}
              <HoverImage
                style={myStyle}
                src={demoLink.imgPath1}
                hoverSrc={demoLink.imgPath2}
              />
            </ReactLink>
          </Grid>
        ))}
      </Grid>
    </>
  );
}

function AcknowledgmentsSection(): React.ReactElement {
  return (
    <>
      <Title anchor="acknowledgments" name="Resources" />
      <Typography variant="body1" align="left" paragraph>
        <li>
          <Link href="https://www.eecs.yorku.ca/~kamel/sidd/">
            SIDD datasets
          </Link>
        </li>
        <li>
          <Link href="https://github.com/BorealisAI/noise_flow">
            Noise Flow
          </Link>
        </li>
        <li>
          <Link href="https://github.com/AbdoKamel/simple-camera-pipeline">
            Simple Camera Pipeline
          </Link>
        </li>
        <li>
          <Link href="https://github.com/leVirve/projectize">
            Project template
          </Link>
          &thinsp;providing by Hung-Jin Lin
        </li>
      </Typography>
    </>
  );
}

function ReferencesSection(): React.ReactElement {
  const references = [
    {
      number: `[1] `,
      link: `https://arxiv.org/abs/1908.08453`,
      text: `"Noise Flow: Noise Modeling with Conditional Normalizing Flows"`,
      suffix: ` , Abdelrahman Abdelhamed, Marcus A. Brubaker, Michael S. Brown (ICCV 2019)`,
    },
    {
      number: `[2] `,
      link: `https://openaccess.thecvf.com/content_cvpr_2018/papers/Abdelhamed_A_High-Quality_Denoising_CVPR_2018_paper.pdf`,
      text: `"A high-quality denoising dataset for smart-phone cameras"`,
      suffix: ` , Abdelhamed, Abdelrahman and Lin, Stephen and Brown, Michael S (CVPR 2018)`,
    },
    {
      number: `[3] `,
      link: `http://openaccess.thecvf.com/content_CVPRW_2019/papers/NTIRE/Abdelhamed_NTIRE_2019_Challenge_on_Real_Image_Denoising_Methods_and_Results_CVPRW_2019_paper.pdf`,
      text: `"Ntire 2019 challenge on real image denoising: Methods and results"`,
      suffix: ` , Abdelhamed, Abdelrahman and Timofte, Radu and Brown, Michael S (CVPR 2019 Workshop)`,
    },
    {
      number: `[4] `,
      link: `https://ieeexplore.ieee.org/iel5/83/4623174/04623175.pdf?casa_token=lqod14Up1QwAAAAA:HKaCMVvbAJWJRsr00pVzanqSAOeqZBKGeRiQFdpmQ-9qr-scr7fDrUYPWrVCs0V5wH-BfN4gpWgu`,
      text: `"Practical Poissonian-Gaussian noise modeling and fitting for single-image raw-data"`,
      suffix: ` , Foi, Alessandro and Trimeche, Mejdi and Katkovnik, Vladimir and Egiazarian, Karen`,
    },
    {
      number: `[5] `,
      link: `http://openaccess.thecvf.com/content_CVPRW_2019/papers/NTIRE/Liu_Learning_Raw_Image_Denoising_With_Bayer_Pattern_Unification_and_Bayer_CVPRW_2019_paper.pdf`,
      text: `"Learning raw image denoising with bayer pattern unification and bayer preserving augmentation"`,
      suffix: ` , Liu, Jiaming and Wu, Chi-Hao and Wang, Yuzhi and Xu, Qin and Zhou, Yuqian and Huang, Haibin and Wang, Chuan and Cai, Shaofan and Ding, Yifan and Fan, Haoqiang and others (CVPR 2019 Workshop)`,
    },
    {
      number: `[6] `,
      link: `https://ieeexplore.ieee.org/iel7/83/4358840/07839189.pdf?casa_token=0BchtjrwPDgAAAAA:58lK5T8JvhJEJaQ0jMkDfKR5zVdqxywrB-_mUHfDULdCsE5gHx-L1Nb4r2Nm4jx1UAOFvuj0jbRd`,
      text: `"Beyond a gaussian denoiser: Residual learning of deep cnn for image denoising"`,
      suffix: ` , Zhang, Kai and Zuo, Wangmeng and Chen, Yunjin and Meng, Deyu and Zhang, Lei`,
    },
  ];
  return (
    <>
      <Title anchor="references" name="References" />
      <Typography align="left" variant="body1" color="inherit" gutterBottom>
        {references.map((reference) => (
          <>
            {reference.number}
            <Link href={reference.link} target="_blank" rel="noopener">
              {reference.text}
            </Link>
            {reference.suffix} <br />
          </>
        ))}
      </Typography>
    </>
  );
}
